import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { cn } from "@msuite/ui/utils";
import {
	Fragment,
	type KeyboardEvent as ReactKeyboardEvent,
	type PointerEvent as ReactPointerEvent,
	useCallback,
	useRef,
} from "react";
import {
	clampToKept,
	isSeam,
	type KeptRange,
	keptDuration,
	moveBoundary,
	type Removal,
	removalsOf,
	restoreRemoval,
} from "./cut-segments";

/** One still along the source, as the editor route signed it. */
export interface CutTimelineStill {
	time: number;
	url: string;
}

/** Props Interface */
interface CutVideoTimelineProps {
	/** The source's length in seconds — the timeline's full width. */
	duration: number;
	/** The stretches the cut keeps, ordered and disjoint. */
	segments: KeptRange[];
	/** Where the player's playhead currently stands. */
	currentTime: number;
	stills: CutTimelineStill[];
	isDisabled?: boolean;
	onChange: (segments: KeptRange[]) => void;
	onSeek: (time: number) => void;
}

/** One arrow key's worth of movement, and one held with shift for crossing a long recording. */
const KEY_STEP_SECONDS = 1;
const KEY_STEP_SECONDS_FAST = 10;

/**
 * The source, laid out end to end, with what the cut removes drawn on it.
 *
 * It models **removals**, not clips. What is hatched in red is what goes; what is left is what
 * readers will see, and the count beside the timeline says how much that is. Trimming an end and
 * taking a stretch out of the middle are the same thing here — a removal with a draggable edge on
 * each side — which is why there is one gesture rather than two tools. Dragging seeks the player,
 * so a boundary is chosen by looking at the frame rather than by guessing at a number.
 *
 * The scissors removes nothing. It divides the range the playhead is in and leaves the two halves
 * meeting at a **seam**: two edges standing at one place, drawn as a pair so that each of them can
 * be grabbed. Pulling one away is what opens a removal, and how far is settled by watching the
 * frame — which is the whole reason a boundary is dragged rather than typed.
 *
 * Removals are derived from the kept ranges rather than stored beside them: two records of one
 * fact are two records that can disagree.
 *
 * A removal carries a restore control rather than a close control, and the difference is not
 * cosmetic: the red region *is* the deletion, so a cross on it would read as deleting the
 * deletion. It is neutral rather than red as well — the red belongs to the region and means "this
 * goes", and a second red thing competing for that meaning makes both of them quieter.
 */
export function CutVideoTimeline({
	duration,
	segments,
	currentTime,
	stills,
	isDisabled = false,
	onChange,
	onSeek,
}: CutVideoTimelineProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const trackRef = useRef<HTMLDivElement>(null);

	/** Functions */
	const timeAt = useCallback(
		(clientX: number) => {
			const track = trackRef.current;
			if (!track) return 0;
			const bounds = track.getBoundingClientRect();
			const fraction = (clientX - bounds.left) / bounds.width;
			return Math.min(Math.max(fraction, 0), 1) * duration;
		},
		[duration],
	);

	/**
	 * Drag one edge for as long as the pointer is down.
	 *
	 * The pointer is captured on the handle where that is possible, so the drag survives leaving
	 * the track — a boundary near either end is otherwise unreachable, because the pointer runs out
	 * of timeline before the handle reaches it. Capture can be refused (a pointer the browser no
	 * longer considers active throws `InvalidPointerId`), so the listeners go on the window in that
	 * case: a drag that tracks slightly differently is worth having, a drag that never starts is
	 * not.
	 */
	function handleOnDrag(index: number, edge: "start" | "end") {
		return (event: ReactPointerEvent<HTMLElement>) => {
			if (isDisabled) return;
			event.preventDefault();
			event.stopPropagation();
			const handle = event.currentTarget;
			let target: HTMLElement | Window = handle;
			try {
				handle.setPointerCapture(event.pointerId);
			} catch {
				target = window;
			}

			const move = (moveEvent: PointerEvent) => {
				const next = moveBoundary(segments, index, edge, timeAt(moveEvent.clientX), duration);
				const moved = next[index];
				if (!moved) return;
				onChange(next);
				onSeek(edge === "start" ? moved.startTime : moved.endTime);
			};
			const stop = () => {
				target.removeEventListener("pointermove", move as EventListener);
				target.removeEventListener("pointerup", stop);
				target.removeEventListener("pointercancel", stop);
			};
			target.addEventListener("pointermove", move as EventListener);
			target.addEventListener("pointerup", stop);
			target.addEventListener("pointercancel", stop);
		};
	}

	/**
	 * Move one edge from the keyboard. The handles are the only way to set a boundary, so without
	 * this the whole feature needs a pointer — and a cut is exactly the kind of precise edit
	 * somebody would rather nudge a frame at a time than drag.
	 */
	function handleOnKey(index: number, edge: "start" | "end") {
		return (event: ReactKeyboardEvent<HTMLElement>) => {
			if (isDisabled) return;
			const direction = event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0;
			const isHome = event.key === "Home";
			const isEnd = event.key === "End";
			if (!direction && !isHome && !isEnd) return;
			event.preventDefault();

			const segment = segments[index];
			if (!segment) return;
			const from = edge === "start" ? segment.startTime : segment.endTime;
			const step = (event.shiftKey ? KEY_STEP_SECONDS_FAST : KEY_STEP_SECONDS) * direction;
			// Home and End ask for the extremes; `moveBoundary` decides where that actually lands,
			// so a key can no more produce an invalid list than a drag can.
			const wanted = isHome ? 0 : isEnd ? duration : from + step;
			const next = moveBoundary(segments, index, edge, wanted, duration);
			const moved = next[index];
			if (!moved) return;
			onChange(next);
			onSeek(edge === "start" ? moved.startTime : moved.endTime);
		};
	}

	/**
	 * Clicking the track moves the playhead — but only within what the cut keeps. A click in a
	 * removed stretch lands on its nearest boundary instead, which both refuses the position and
	 * shows where the edge is.
	 */
	function handleOnScrub(event: ReactPointerEvent<HTMLDivElement>) {
		if (isDisabled) return;
		onSeek(clampToKept(segments, timeAt(event.clientX)));
	}

	/** Computed */
	const percent = (time: number) => `${(time / duration) * 100}%`;
	const removals = removalsOf(segments, duration);

	/** Render */
	return (
		<div className="v-stack gap-2">
			<div
				ref={trackRef}
				onPointerDown={handleOnScrub}
				className={cn(
					"relative h-16 w-full select-none overflow-hidden rounded-md border-color border",
					isDisabled ? "opacity-50" : "cursor-pointer",
				)}
			>
				<div className="flex h-full w-full">
					{stills.map((still) => (
						<img
							key={still.time}
							src={still.url}
							alt=""
							draggable={false}
							className="h-full min-w-0 flex-1 object-cover"
						/>
					))}
				</div>

				{removals.map((removal) => (
					<RemovalRegion
						key={removal.after}
						label={t("videos.cut_restore")}
						left={percent(removal.startTime)}
						width={percent(removal.endTime - removal.startTime)}
						align={alignmentOf(removal, segments.length)}
						onRestore={() => onChange(restoreRemoval(segments, removal, duration))}
					/>
				))}

				{/* Keyed by position, never by the times being dragged: a key derived from a boundary
				    changes the instant that boundary moves, and React then replaces the very element
				    the pointer is captured on — the drag would end on its first frame. */}
				{segments.map((segment, index) => (
					<Fragment key={index}>
						<Handle
							position={percent(segment.startTime)}
							side={sideOf(segment.startTime, duration, isSeam(segments, index - 1) && "after")}
							onPointerDown={handleOnDrag(index, "start")}
							onKeyDown={handleOnKey(index, "start")}
							label={t("videos.cut_start_handle")}
							value={segment.startTime}
							max={duration}
						/>
						<Handle
							position={percent(segment.endTime)}
							side={sideOf(segment.endTime, duration, isSeam(segments, index) && "before")}
							onPointerDown={handleOnDrag(index, "end")}
							onKeyDown={handleOnKey(index, "end")}
							label={t("videos.cut_end_handle")}
							value={segment.endTime}
							max={duration}
						/>
					</Fragment>
				))}

				{/* The playhead reads as one mark, the way it does in editing software: a single blue
				    bar centred on the position. Drawn as a hairline it needed an outline to survive
				    the stills behind it, and the outline was what the eye picked up — two dark
				    strokes with a gap, rather than a line. */}
				<div
					className="-translate-x-1/2 pointer-events-none absolute inset-y-0 w-[3px] rounded-full bg-blue-500"
					// Clamped by half its own width at each end: centred on the position, the bar would
					// otherwise be half outside the track's clip box at 0 and at the end — and 0 is
					// where every video starts, so the thin half is the one that would be seen most.
					style={{ left: `clamp(1.5px, ${percent(currentTime)}, calc(100% - 1.5px))` }}
				/>
			</div>

			<div className="h-stack justify-between text-mauve-11 text-xs">
				<span>{formatClock(currentTime)}</span>
				<span>
					{formatClock(duration)} → <strong>{formatClock(keptDuration(segments))}</strong>
				</span>
			</div>
		</div>
	);
}

/**
 * Which way a handle leans on its position.
 *
 * Centred, except where centring would make half of it unreachable. At a seam two edges share one
 * place, and stacked only the one drawn last could be grabbed. At the source's own start and end
 * the track clips what hangs over, leaving six pixels of a twelve-pixel target — and those two are
 * the handles every trim starts from.
 *
 * @param time - Where the handle sits.
 * @param duration - The source's length.
 * @param seam - Which way the seam rule wants it, or false when this edge is not on one.
 * @returns The side to lean to.
 */
function sideOf(time: number, duration: number, seam: "before" | "after" | false) {
	if (seam) return seam;
	if (time <= 0) return "after" as const;
	if (time >= duration) return "before" as const;
	return "center" as const;
}

/**
 * Where a removal's restore control sits: away from the drag handles it borders.
 *
 * A removal at either end has a handle on one side only, so the control goes to the other. One in
 * the middle has a handle on both, so it goes between them — a removal of two seconds has no room
 * to spare, and an edge the control covered could not be grabbed.
 */
function alignmentOf(removal: Removal, count: number) {
	if (removal.after === -1) return "left" as const;
	if (removal.after === count - 1) return "right" as const;
	return "center" as const;
}

/** Props Interface */
interface RemovalRegionProps {
	label: string;
	left: string;
	width: string;
	align: "left" | "right" | "center";
	onRestore: () => void;
}

/** One stretch the cut removes: hatched red, with a control that puts it back. */
function RemovalRegion({ label, left, width, align, onRestore }: RemovalRegionProps) {
	return (
		// `cursor-default` rather than the track's hand: the playhead does not go in here, so
		// advertising the whole hatched stretch as a target promises something it does not do. The
		// one thing inside it that *is* a target says so for itself.
		<div
			className="absolute inset-y-0 cursor-default bg-[repeating-linear-gradient(45deg,rgba(220,38,38,0.55)_0px,rgba(220,38,38,0.55)_6px,rgba(220,38,38,0.25)_6px,rgba(220,38,38,0.25)_12px)]"
			style={{ left, width }}
		>
			<button
				type="button"
				title={label}
				aria-label={label}
				onClick={onRestore}
				onPointerDown={(event) => event.stopPropagation()}
				className={cn(
					// Above the handles, and only as tall as it needs to be. A removal narrower than
					// a handle is completely covered by the two that flank it, and that is the
					// ordinary case — a stumble taken out of a five-minute recording is a few
					// seconds of a very wide timeline. The control keeps the top of the region and
					// the handles keep the rest, so both stay reachable at any width.
					"absolute top-1 z-10 flex-center cursor-pointer rounded bg-mauve-12/70 p-1 text-white",
					"hover:bg-mauve-12",
					align === "left" && "left-1",
					align === "right" && "right-1",
					align === "center" && "-translate-x-1/2 left-1/2",
				)}
			>
				<Icon
					name="RotateCcw"
					size={12}
				/>
			</button>
		</div>
	);
}

/** Props Interface */
interface HandleProps {
	position: string;
	/**
	 * Which way the handle sits on its position. Centred everywhere except at a seam, where two
	 * edges share one place and the pair stands shoulder to shoulder rather than stacked — stacked,
	 * only the one React painted last could ever be grabbed.
	 */
	side: "center" | "before" | "after";
	label: string;
	/** Where this edge sits, in seconds — announced, so the value is not only a pixel offset. */
	value: number;
	max: number;
	onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
	onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
}

/**
 * One edge of a kept stretch: wide enough to grab without being wide enough to hide the frame
 * behind it, and a slider rather than a button, so what it is and where it stands are announced
 * and the arrow keys mean what they usually mean.
 */
function Handle({ position, side, label, value, max, onPointerDown, onKeyDown }: HandleProps) {
	return (
		<button
			type="button"
			role="slider"
			aria-label={label}
			aria-valuemin={0}
			aria-valuemax={Math.round(max)}
			aria-valuenow={Math.round(value)}
			aria-valuetext={formatClock(value)}
			title={label}
			onPointerDown={onPointerDown}
			onKeyDown={onKeyDown}
			style={{ left: position }}
			className={cn(
				"absolute inset-y-0 w-3 cursor-ew-resize touch-none bg-white/90 shadow-[0_0_0_1px_rgba(0,0,0,0.45)] focus-visible:bg-accent-400",
				side === "center" && "-translate-x-1/2",
				// The two halves of a seam carry a dark edge on the side they meet on, so the pair
				// reads as one cut mark. Without it a fresh cut is two white handles on a bright
				// still with the playhead standing over them — the gesture would look like nothing
				// happened at all.
				side === "before" && "-translate-x-full border-mauve-12 border-r-2",
				side === "after" && "border-mauve-12 border-l-2",
			)}
		/>
	);
}

/** `m:ss`, which is what a tutorial's length reads as. */
function formatClock(seconds: number) {
	const whole = Math.max(0, Math.round(seconds));
	return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}
